import type { DataTableSelectEvent } from "primereact/datatable";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetTablesStructureQuery, useLazyGetTableValueQuery } from "../../../api/rtk/base/slices/tablesApiSlice";
import { getValueWithMapLinks, prepareTableData } from "../../../features/dataFormatFunctions/dataFormatter";
import { getItemWebStorage } from "../../../features/webStorageSaver";
import LoaderWrapper from "../../../widgets/loaderWrapper/LoaderWrapper";
import { prepareQueryArgsForTableFetch } from "../../../widgets/table/tableFeatures/tableFetchFunctions";
import { defineTableColumnsFromColumnsStructureObject, configureTableLazyState, defineTableFilters, TableEvents, tableStateModifyHandle } from "../../../widgets/table/tableFeatures/tableFunctions";
import { getBasicTablePaginator } from "../../../widgets/table/tableFeatures/tableTemplates/tablePaginatorTemplates";
import TableWidget from "../../../widgets/table/tableWidget/TableWidget";
import TableInfoWidget from "../../../widgets/table/tableAddons/tableInfoWidget/TableInfoWidget";
import { useAppSelector } from "../../../stateManagement/hooks/redux";
import { exportTypes, TableExportComponent } from "../../../widgets/tablesShared/tableTools/tableExportComponent";
import ContentHeader from "../../../widgets/contentHeader/ContentHeader";
import { useDispatch } from "react-redux";
import { setTableCaption, setTableDataCount, showToastMessage } from "../../../stateManagement/reducers";
import ToastMessageContainer from "../../../widgets/toastMessageContainer/ToastMessageContainer";

export default function TableObjectType() {
    const navigate = useNavigate()
    const { objectTypeName }: any = useParams();
    const dispatch = useDispatch()
    const [tableTitle, setTableTitle] = useState<string>('');
    const [columns, setColumns] = useState<any>(null)
    const [tableState, setTableState] = useState<any>(null)
    const [tableValueUrl, setTableValueUrl] = useState<string>('')
    const [paginator, setPaginator] = useState<any>({})
    const [paginatorPageInputTooltip, setPaginatorPageInputTooltip] = useState<any>('Введите номер страницы.')
    const [tableValue, setTableValue] = useState<any>([])
    const [tableLoading, setTableLoading] = useState<boolean>(true)
    const [tableStructureError, setTableStructureError] = useState<boolean>(false)
    const objectTypes = getItemWebStorage('objectTypes')
    const { pathId } = useAppSelector(state => state.pathsInfo.selectedPath) ?? {}
    const tableInfoWindowIsShown = useAppSelector(state => state.contentHeader.tableInfoWindowIsShown)
    const dataCount = useAppSelector(state => state.table.dataCount)
    const currentConfig = getItemWebStorage("currentConfig");
    const structureSuffix = currentConfig?.structureSuffix
    const isRoadsListPageAvailable = currentConfig?.menuShow?.road;

    useEffect(() => {
        const caption = objectTypes.find((objectType: any) => objectType.name === objectTypeName)?.caption
        dispatch(setTableCaption(caption))
        caption ? setTableTitle(`Таблица «${caption}»`) : setTableTitle('Таблица «...»')
    }, [objectTypeName])

    const {
        data: tablesStructure,
        isError: isErrorTableStructure,
    } = useGetTablesStructureQuery({ structureSuffix })

    const [fetchTableValue, { isError: isErrorTableValue }] = useLazyGetTableValueQuery()

    useEffect(() => {
        const paginatorTemplate = getBasicTablePaginator(paginatorPageInputTooltip, setPaginatorPageInputTooltip, tableState, setTableState, dataCount, objectTypeName)
        setPaginator(paginatorTemplate)
    }, [dataCount, tableState, paginatorPageInputTooltip])

    useEffect(() => {
        if (tablesStructure && objectTypeName) {
            let tableStructure: any = tablesStructure[objectTypeName]
            if (tableStructure) {
                let columns = defineTableColumnsFromColumnsStructureObject(tableStructure)
                let filters = defineTableFilters(columns)
                setColumns(columns)
                setTableState(getItemWebStorage("tablesState")?.[objectTypeName] ?? configureTableLazyState({ filters }))
                const tableValueUrl = tableStructure?.url.substring(1)
                setTableValueUrl(tableValueUrl)
            } else {
                restartTable()
                setTableStructureError(true)
            }
        }
    }, [tablesStructure, objectTypeName])

    function restartTable() {
        setColumns(null)
        setTableState(null)
        dispatch(setTableDataCount(undefined))
    }

    async function getTableValue() {
        setTableLoading(true)
        const { sortString, filterStringArray, page }: any = prepareQueryArgsForTableFetch(tableState, columns)
        isRoadsListPageAvailable && filterStringArray.push(`pathId eq ${pathId}`)
        const { data: tableValueResponse } = await fetchTableValue({ tableValueUrl, pages: true, top: 100, order: sortString, filterStringArray, skip: page * 100 })
        let value = tableValueResponse?.value
        value = getValueWithMapLinks(value, objectTypeName)
        value = prepareTableData(value, columns)
        setTableValue(value)
        let dataCount = tableValueResponse?.['@xdata.count']
        dispatch(setTableDataCount(dataCount))
        setTableLoading(false)
    }

    useEffect(() => {
        if (tableValueUrl && columns) {
            getTableValue()
        }
    }, [tableValueUrl, columns, tableState])

    const onSort = (state: any) => tableStateModifyHandle(state, TableEvents.sort, setTableState, objectTypeName)
    const onPage = (state: any) => tableStateModifyHandle(state, TableEvents.page, setTableState, objectTypeName)
    const onFilter = (state: any) => tableStateModifyHandle(state, TableEvents.filter, setTableState, objectTypeName)

    useEffect(() => {
        if (isErrorTableStructure || isErrorTableValue || tableStructureError) {
            setTableLoading(false)
            setTableStructureError(false)
        }
    }, [isErrorTableStructure, isErrorTableValue, tableStructureError])

    function onRowSelect(event: DataTableSelectEvent) {
        navigate(`${event.data.InfoObjectID}`);
    }

    useEffect(() => {
        if (isErrorTableValue || tableStructureError || isErrorTableStructure) {
            dispatch(showToastMessage({ messageText: "Ошибка загрузки таблицы", messageType: "error" }))
        }
    }, [isErrorTableValue, tableStructureError, isErrorTableStructure])

    return (
        <>
            <ContentHeader title={tableTitle} type="table" option="objectType" />
            <div className="table-widget-with-toast with-content-header">
                <ToastMessageContainer />
                <LoaderWrapper isLoading={tableLoading} >
                    {tableInfoWindowIsShown && <TableInfoWidget />}
                    {exportTypes.map((exportType: any) =>
                        <TableExportComponent dataCount={dataCount} tableState={tableState} columns={columns} objectType={objectTypeName} url={tableValueUrl} pathId={pathId} exportType={exportType} exportName={tableTitle} />
                    )}
                    <TableWidget
                        tableValue={tableValue}
                        tableState={tableState}
                        columns={columns}
                        onRowSelect={onRowSelect}
                        dataCount={dataCount}
                        paginatorTemplate={paginator}
                        pageSize={100}
                        onPage={onPage}
                        onSort={onSort}
                        onFilter={onFilter}
                        isLazy={true}
                    />
                </LoaderWrapper>
            </div >
        </>
    )
}

